import { resolveId } from '@medplum/core'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { InboxItemType } from '@seen/core/models/inbox'
import { isOfType } from '@seen/core/utils'
import { useQueryClient } from '@tanstack/react-query'
import { chunk } from 'lodash-es'
import { useMemo } from 'react'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import type { NotificationItem } from '#src/hooks/useNotificationItems.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

/*
 * Resolves parent resource references for legacy comment notifications
 * that were sent before the parentResource enrichment was deployed.
 *
 * For each un-enriched comment notification, fetches the Communication
 * resource and walks its partOf chain to find the parent Task/Order/etc.
 * Returns a map of notification ID -> parent resource reference string.
 *
 * This is a transitional measure: once all legacy notifications have
 * been archived or replaced, this hook becomes a no-op.
 *
 * Batching: rather than firing one readReference per item (O(N) concurrent
 * requests), this hook issues a single searchResourceClass call per chunk
 * of 50 IDs, then seeds the React Query read-cache so that any downstream
 * useRead calls for those Communication resources hit the cache immediately.
 */

const BATCH_CHUNK_SIZE = 50

export function useLegacyCommentDedupeKeys(
  items: NotificationItem[]
): Map<string, string> {
  const medplum = useMedplum()
  const queryClient = useQueryClient()

  /* Filter to un-enriched comment notifications that need resolution */
  const legacyCommentItems = useMemo(
    () =>
      items.filter((item) => {
        const { data } = item
        if (
          data.type !== InboxItemType.NewComment &&
          data.type !== InboxItemType.NewCommentReply &&
          data.type !== InboxItemType.CommentMention
        ) {
          return false
        }
        return !data.parentResource
      }),
    [items]
  )

  /* Build a stable map of notificationId -> commentId for the query */
  const notifToCommentId = useMemo(() => {
    const map = new Map<string, string>()
    for (const item of legacyCommentItems) {
      const commentRef =
        item.data.type === InboxItemType.NewComment ||
        item.data.type === InboxItemType.NewCommentReply ||
        item.data.type === InboxItemType.CommentMention
          ? item.data.comment
          : undefined
      if (!commentRef) {
        continue
      }
      const id = resolveId(commentRef)
      if (id) {
        map.set(item.id, id)
      }
    }
    return map
  }, [legacyCommentItems])

  /* Stable query key: sorted unique comment IDs */
  const commentIds = useMemo(
    () => [...new Set(notifToCommentId.values())].sort(),
    [notifToCommentId]
  )

  const { data } = useQuery({
    queryKey: ['inbox', 'legacy-comment-dedupe', commentIds] as const,
    queryFn: async () => {
      const allCommunications: CommentCommunication[] = []
      for (const ids of chunk(commentIds, BATCH_CHUNK_SIZE)) {
        const resources = await medplum.searchResourceClass(
          CommentCommunication,
          { _id: ids.join(','), _count: BATCH_CHUNK_SIZE }
        )
        /* Seed individual read cache so downstream useRead calls are instant */
        for (const resource of resources) {
          if (resource.id) {
            queryClient.setQueryData(
              cacheKeys.read('Communication', resource.id),
              resource.toFhirResource()
            )
          }
        }
        allCommunications.push(...resources)
      }

      /* Build a map from commentId -> parentRef by walking the partOf chain */
      const commentIdToParentRef = new Map<string, string>()
      for (const resource of allCommunications) {
        if (!resource.id) {
          continue
        }
        for (const ref of resource.partOf) {
          if (!isOfType(ref, 'Communication') && ref.reference) {
            commentIdToParentRef.set(resource.id, ref.reference)
            break
          }
        }
      }

      return commentIdToParentRef
    },
    enabled: commentIds.length > 0,
    staleTime: Number.POSITIVE_INFINITY,
  })

  return useMemo(() => {
    if (!data) {
      return new Map<string, string>()
    }
    const result = new Map<string, string>()
    for (const [notifId, commentId] of notifToCommentId) {
      const parentRef = data.get(commentId)
      if (parentRef) {
        result.set(notifId, parentRef)
      }
    }
    return result
  }, [data, notifToCommentId])
}
